/**
 * 🚀 SPACE SURVIVAL LOCKER - Coffre de Survie Spatial
 * Design "Celestial Cabin VIP" - Équipements du Vaisseau
 * 
 * ✅ TOUTES les fonctionnalités conservées :
 * - 11 catégories d'apps
 * - Mélodies musicales
 * - Sélecteur d'instrument
 * - Bordures lumineuses animées
 * 
 * 🆕 NOUVEAU :
 * - Coffre spatial qui s'ouvre/ferme
 * - Son de dépressurisation à l'ouverture
 * - Design cabine de vaisseau
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ExternalLink, ChevronDown, ChevronUp, Lock, Unlock,
  Dices, Trophy, Gamepad2,
  Camera, MessageCircle, Users, Share2, Hash, Briefcase, Radio as RadioIcon,
  Music, Headphones, Mic2, ListMusic,
  Tv, Film, Play, Clapperboard, MonitorPlay, Video,
  ShoppingBag, ShoppingCart, Tag, Store, Package, Shirt,
  UtensilsCrossed, Pizza, Coffee, Truck, ChefHat,
  Search, Mail, MapPin, Cloud, Globe, Calculator,
  Car, Train, Navigation, Bike, Plane
} from 'lucide-react';
import { useTheme } from './ThemeToggle';
import smartMelody, { INSTRUMENTS } from './SmartMelody';
import InstrumentSelector from './InstrumentSelector';

// Configuration des apps avec icônes Lucide personnalisées
const APP_CATEGORIES = [
  {
    id: 'betting',
    name: '🔥 All-in ou rien !',
    emoji: '🎰',
    color: 'from-amber-500 to-orange-600',
    apps: [
      { name: 'Paris Sportifs', icon: Trophy, url: 'https://www.betclic.fr', color: '#E30613', bgGradient: 'from-red-500 to-red-700' },
      { name: 'Courses', icon: Trophy, url: 'https://www.pmu.fr', color: '#00A651', bgGradient: 'from-green-500 to-green-700' },
      { name: 'Loterie', icon: Dices, url: 'https://www.fdj.fr', color: '#004C97', bgGradient: 'from-blue-600 to-blue-800' },
      { name: 'Poker', icon: Gamepad2, url: 'https://www.winamax.fr', color: '#C8102E', bgGradient: 'from-red-600 to-rose-700' },
      { name: 'Pronostics', icon: Trophy, url: 'https://www.unibet.fr', color: '#147B45', bgGradient: 'from-emerald-500 to-emerald-700' },
      { name: 'Tournois', icon: Gamepad2, url: 'https://www.pokerstars.fr', color: '#C00000', bgGradient: 'from-rose-600 to-rose-800' },
    ]
  },
  {
    id: 'social',
    name: '💜 Squad Goals',
    emoji: '📱',
    color: 'from-pink-500 to-purple-600',
    apps: [
      { name: 'Photos', icon: Camera, url: 'https://www.instagram.com', color: '#E4405F', bgGradient: 'from-pink-500 via-purple-500 to-orange-400' },
      { name: 'Vidéos Courtes', icon: Music, url: 'https://www.tiktok.com', color: '#000000', bgGradient: 'from-gray-900 to-gray-800' },
      { name: 'Stories', icon: Camera, url: 'https://www.snapchat.com', color: '#FFFC00', bgGradient: 'from-yellow-400 to-yellow-500' },
      { name: 'Moments', icon: Camera, url: 'https://bereal.com', color: '#000000', bgGradient: 'from-gray-800 to-gray-900' },
      { name: 'Talkie-Walkie', icon: Mic2, url: 'https://tenten.app', color: '#FF6B6B', bgGradient: 'from-red-400 to-red-600' },
      { name: 'Actualités', icon: Hash, url: 'https://www.x.com', color: '#000000', bgGradient: 'from-gray-900 to-black' },
      { name: 'Amis & Famille', icon: Users, url: 'https://www.facebook.com', color: '#1877F2', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Inspirations', icon: Share2, url: 'https://www.pinterest.fr', color: '#E60023', bgGradient: 'from-red-500 to-red-700' },
      { name: 'Pro & Réseau', icon: Briefcase, url: 'https://www.linkedin.com', color: '#0A66C2', bgGradient: 'from-blue-600 to-blue-800' },
      { name: 'Communautés', icon: MessageCircle, url: 'https://discord.com', color: '#5865F2', bgGradient: 'from-indigo-500 to-indigo-700' },
      { name: 'Gaming Live', icon: Video, url: 'https://www.twitch.tv', color: '#9146FF', bgGradient: 'from-purple-500 to-purple-700' },
      { name: 'Conversations', icon: MessageCircle, url: 'https://www.threads.net', color: '#000000', bgGradient: 'from-gray-800 to-black' },
    ]
  },
  {
    id: 'music',
    name: '🎶 Pump le son !',
    emoji: '🎵',
    color: 'from-green-500 to-emerald-600',
    apps: [
      { name: 'Streaming', icon: Headphones, url: 'https://open.spotify.com', color: '#1DB954', bgGradient: 'from-green-500 to-green-700' },
      { name: 'Musique FR', icon: Music, url: 'https://www.deezer.com', color: '#FEAA2D', bgGradient: 'from-amber-400 to-orange-500' },
      { name: 'Hi-Fi', icon: Headphones, url: 'https://music.apple.com', color: '#FA243C', bgGradient: 'from-red-500 to-pink-600' },
      { name: 'Clips & Sons', icon: Play, url: 'https://music.youtube.com', color: '#FF0000', bgGradient: 'from-red-500 to-red-700' },
      { name: 'Indépendant', icon: ListMusic, url: 'https://soundcloud.com', color: '#FF5500', bgGradient: 'from-orange-500 to-orange-700' },
      { name: 'Audiophile', icon: Headphones, url: 'https://tidal.com', color: '#000000', bgGradient: 'from-gray-800 to-black' },
    ]
  },
  {
    id: 'video',
    name: '🍿 Mode canapé ON',
    emoji: '📺',
    color: 'from-red-500 to-rose-600',
    apps: [
      { name: 'Vidéos', icon: Play, url: 'https://www.youtube.com', color: '#FF0000', bgGradient: 'from-red-500 to-red-700' },
      { name: 'Séries & Films', icon: Film, url: 'https://www.netflix.com', color: '#E50914', bgGradient: 'from-red-600 to-red-800' },
      { name: 'Animation', icon: Clapperboard, url: 'https://www.disneyplus.com', color: '#113CCF', bgGradient: 'from-blue-600 to-indigo-800' },
      { name: 'Prime', icon: MonitorPlay, url: 'https://www.primevideo.com', color: '#00A8E1', bgGradient: 'from-cyan-500 to-blue-600' },
      { name: 'Sport & Ciné', icon: Tv, url: 'https://www.canalplus.com', color: '#000000', bgGradient: 'from-gray-800 to-black' },
      { name: 'Live Gaming', icon: Video, url: 'https://www.twitch.tv', color: '#9146FF', bgGradient: 'from-purple-500 to-purple-700' },
    ]
  },
  {
    id: 'shopping',
    name: '💳 Shut up & take my money',
    emoji: '🛒',
    color: 'from-blue-500 to-cyan-600',
    apps: [
      { name: 'Tout en ligne', icon: Package, url: 'https://www.amazon.fr', color: '#FF9900', bgGradient: 'from-orange-400 to-orange-600' },
      { name: 'Seconde main', icon: Shirt, url: 'https://www.vinted.fr', color: '#09B1BA', bgGradient: 'from-teal-400 to-teal-600' },
      { name: 'Petites annonces', icon: Tag, url: 'https://www.leboncoin.fr', color: '#F56B2A', bgGradient: 'from-orange-500 to-orange-700' },
      { name: 'Bonnes affaires', icon: ShoppingBag, url: 'https://www.temu.com/fr', color: '#FF6F00', bgGradient: 'from-orange-500 to-red-500' },
      { name: 'Import', icon: Store, url: 'https://fr.aliexpress.com', color: '#E62E04', bgGradient: 'from-red-500 to-red-700' },
      { name: 'Mode', icon: Shirt, url: 'https://www.zalando.fr', color: '#FF6900', bgGradient: 'from-orange-500 to-orange-700' },
    ]
  },
  {
    id: 'food',
    name: '🤤 Ventre qui gargouille',
    emoji: '🍔',
    color: 'from-orange-500 to-red-600',
    apps: [
      { name: 'Livraison repas', icon: UtensilsCrossed, url: 'https://www.ubereats.com', color: '#06C167', bgGradient: 'from-green-500 to-green-700' },
      { name: 'Restos livrés', icon: Truck, url: 'https://deliveroo.fr', color: '#00CCBC', bgGradient: 'from-teal-400 to-teal-600' },
      { name: 'Commander', icon: UtensilsCrossed, url: 'https://www.justeat.fr', color: '#F36D00', bgGradient: 'from-orange-500 to-orange-700' },
      { name: 'Burgers', icon: ChefHat, url: 'https://www.mcdonalds.fr', color: '#FFC72C', bgGradient: 'from-yellow-400 to-yellow-600' },
      { name: 'Pizzas', icon: Pizza, url: 'https://www.dominos.fr', color: '#006491', bgGradient: 'from-blue-600 to-blue-800' },
      { name: 'Poulet', icon: ChefHat, url: 'https://www.kfc.fr', color: '#F40027', bgGradient: 'from-red-500 to-red-700' },
    ]
  },
  {
    id: 'tools',
    name: '🧙‍♂️ Abracadabra',
    emoji: '🔧',
    color: 'from-slate-500 to-gray-700',
    apps: [
      { name: 'Recherche', icon: Search, url: 'https://www.google.fr', color: '#4285F4', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Emails', icon: Mail, url: 'https://mail.google.com', color: '#EA4335', bgGradient: 'from-red-500 to-red-600' },
      { name: 'Messagerie', icon: MessageCircle, url: 'https://web.whatsapp.com', color: '#25D366', bgGradient: 'from-green-500 to-green-600' },
      { name: 'Itinéraires', icon: MapPin, url: 'https://maps.google.com', color: '#34A853', bgGradient: 'from-green-500 to-green-700' },
      { name: 'Météo', icon: Cloud, url: 'https://www.meteofrance.com', color: '#0078D4', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Traduction', icon: Globe, url: 'https://translate.google.com', color: '#4285F4', bgGradient: 'from-blue-500 to-indigo-600' },
    ]
  },
  {
    id: 'transport',
    name: '⚡ Vroum vroum !',
    emoji: '🚗',
    color: 'from-indigo-500 to-blue-600',
    apps: [
      { name: 'VTC', icon: Car, url: 'https://www.uber.com', color: '#000000', bgGradient: 'from-gray-800 to-black' },
      { name: 'Courses', icon: Car, url: 'https://bolt.eu', color: '#34D186', bgGradient: 'from-green-400 to-green-600' },
      { name: 'Trains', icon: Train, url: 'https://www.sncf-connect.com', color: '#9B2743', bgGradient: 'from-rose-700 to-rose-900' },
      { name: 'Covoiturage', icon: Users, url: 'https://www.blablacar.fr', color: '#00AFF5', bgGradient: 'from-cyan-400 to-cyan-600' },
      { name: 'Trajets urbains', icon: Navigation, url: 'https://citymapper.com', color: '#35D46A', bgGradient: 'from-green-400 to-green-600' },
      { name: 'Navigation', icon: Navigation, url: 'https://www.waze.com', color: '#33CCFF', bgGradient: 'from-cyan-400 to-cyan-600' },
    ]
  },
  {
    id: 'pro',
    name: '💼 Mode Boss activé',
    emoji: '🏢',
    color: 'from-slate-600 to-zinc-800',
    apps: [
      { name: 'Visio Pro', icon: Video, url: 'https://zoom.us', color: '#2D8CFF', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Équipe', icon: Users, url: 'https://slack.com', color: '#4A154B', bgGradient: 'from-purple-700 to-purple-900' },
      { name: 'Organisation', icon: Package, url: 'https://www.notion.so', color: '#000000', bgGradient: 'from-gray-800 to-black' },
      { name: 'Projets', icon: Briefcase, url: 'https://trello.com', color: '#0079BF', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Docs & Drive', icon: Cloud, url: 'https://workspace.google.com', color: '#4285F4', bgGradient: 'from-blue-500 to-green-500' },
      { name: 'RDV Pro', icon: Calculator, url: 'https://calendly.com', color: '#006BFF', bgGradient: 'from-blue-600 to-blue-800' },
      { name: 'Teams', icon: Video, url: 'https://teams.microsoft.com', color: '#6264A7', bgGradient: 'from-indigo-500 to-indigo-700' },
      { name: 'Design', icon: Camera, url: 'https://www.figma.com', color: '#F24E1E', bgGradient: 'from-orange-500 to-red-600' },
      { name: 'Code', icon: Globe, url: 'https://github.com', color: '#181717', bgGradient: 'from-gray-800 to-black' },
      { name: 'Tableau Excel', icon: Calculator, url: 'https://www.google.fr/intl/fr/sheets/about/', color: '#0F9D58', bgGradient: 'from-green-500 to-green-700' },
    ]
  },
  {
    id: 'ai',
    name: '🤖 Cerveaux augmentés',
    emoji: '🧠',
    color: 'from-violet-500 to-fuchsia-600',
    apps: [
      { name: 'ChatGPT', icon: MessageCircle, url: 'https://chat.openai.com', color: '#10A37F', bgGradient: 'from-emerald-500 to-teal-600' },
      { name: 'Claude', icon: MessageCircle, url: 'https://claude.ai', color: '#D4A574', bgGradient: 'from-amber-400 to-orange-500' },
      { name: 'Gemini', icon: Dices, url: 'https://gemini.google.com', color: '#8E75B2', bgGradient: 'from-purple-400 to-blue-500' },
      { name: 'Perplexity', icon: Search, url: 'https://www.perplexity.ai', color: '#20B2AA', bgGradient: 'from-teal-400 to-cyan-600' },
      { name: 'Midjourney', icon: Camera, url: 'https://www.midjourney.com', color: '#000000', bgGradient: 'from-gray-800 to-black' },
      { name: 'DALL-E', icon: Camera, url: 'https://labs.openai.com', color: '#412991', bgGradient: 'from-indigo-600 to-purple-700' },
      { name: 'Copilot', icon: Globe, url: 'https://copilot.microsoft.com', color: '#0078D4', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Poe', icon: MessageCircle, url: 'https://poe.com', color: '#5B4DC3', bgGradient: 'from-violet-500 to-purple-700' },
      { name: 'Suno (Musique)', icon: Music, url: 'https://suno.ai', color: '#FF6B6B', bgGradient: 'from-pink-500 to-red-500' },
      { name: 'Runway (Vidéo)', icon: Video, url: 'https://runwayml.com', color: '#000000', bgGradient: 'from-gray-800 to-violet-900' },
    ]
  },
  {
    id: 'crypto',
    name: '💰 Wallet to the moon',
    emoji: '🚀',
    color: 'from-yellow-500 to-amber-600',
    apps: [
      { name: 'Binance', icon: Dices, url: 'https://www.binance.com', color: '#F3BA2F', bgGradient: 'from-yellow-400 to-yellow-600' },
      { name: 'Coinbase', icon: Dices, url: 'https://www.coinbase.com', color: '#0052FF', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'KuCoin', icon: Dices, url: 'https://www.kucoin.com', color: '#23AF91', bgGradient: 'from-emerald-500 to-teal-600' },
      { name: 'MetaMask', icon: Package, url: 'https://metamask.io', color: '#E2761B', bgGradient: 'from-orange-500 to-orange-700' },
      { name: 'Trust Wallet', icon: Package, url: 'https://trustwallet.com', color: '#3375BB', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Ledger', icon: Package, url: 'https://www.ledger.com', color: '#000000', bgGradient: 'from-gray-800 to-black' },
      { name: 'CoinMarketCap', icon: Calculator, url: 'https://coinmarketcap.com', color: '#3861FB', bgGradient: 'from-blue-600 to-indigo-700' },
      { name: 'OpenSea', icon: Store, url: 'https://opensea.io', color: '#2081E2', bgGradient: 'from-blue-500 to-blue-700' },
      { name: 'Kraken', icon: Dices, url: 'https://www.kraken.com', color: '#5741D9', bgGradient: 'from-violet-500 to-purple-700' },
      { name: 'Uniswap', icon: Dices, url: 'https://uniswap.org', color: '#FF007A', bgGradient: 'from-pink-500 to-pink-700' },
    ]
  },
];

// 🔊 Son de dépressurisation spatial
const playDepressurizationSound = () => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  // Bruit blanc pour l'air qui s'échappe
  const bufferSize = audioContext.sampleRate * 0.8;
  const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
  const output = noiseBuffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    output[i] = Math.random() * 2 - 1;
  }
  
  const whiteNoise = audioContext.createBufferSource();
  whiteNoise.buffer = noiseBuffer;
  
  // Filtre passe-bas pour un son plus "aérien"
  const lowpass = audioContext.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.setValueAtTime(2000, audioContext.currentTime);
  lowpass.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.6);
  
  // Envelope pour le fade out
  const gainNode = audioContext.createGain();
  gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
  
  whiteNoise.connect(lowpass);
  lowpass.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  whiteNoise.start();
  whiteNoise.stop(audioContext.currentTime + 0.7);
  
  // Son de "clic" mécanique
  setTimeout(() => {
    const clickOsc = audioContext.createOscillator();
    const clickGain = audioContext.createGain();
    clickOsc.frequency.setValueAtTime(150, audioContext.currentTime);
    clickOsc.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.1);
    clickGain.gain.setValueAtTime(0.1, audioContext.currentTime);
    clickGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
    clickOsc.connect(clickGain);
    clickGain.connect(audioContext.destination);
    clickOsc.start();
    clickOsc.stop(audioContext.currentTime + 0.15);
  }, 50);
};

// Composant pour une icône d'app avec Lucide
const AppIcon = ({ app, index, isDarkMode }) => {
  const handleClick = () => {
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  const IconComponent = app.icon;

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.02 }}
      whileHover={{ scale: 1.08, y: -4 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleClick}
      className="flex flex-col items-center gap-1.5 p-1.5 group"
      data-testid={`app-icon-${app.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div 
        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shadow-lg transform transition-all duration-200 group-hover:shadow-xl bg-gradient-to-br ${app.bgGradient}`}
        style={{ boxShadow: `0 6px 20px ${app.color}40` }}
      >
        <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white drop-shadow-md" strokeWidth={2} />
      </div>
      <span className={`text-[10px] sm:text-[11px] text-center leading-tight font-medium transition-colors max-w-[60px] ${
        isDarkMode ? 'text-gray-400 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-900'
      }`}>
        {app.name}
      </span>
    </motion.button>
  );
};

// Composant pour une catégorie d'apps (version compacte)
const AppCategory = ({ category, isExpanded, onToggle, isDarkMode }) => {
  const handleToggle = () => {
    smartMelody.playCategoryMelody(category.id);
    onToggle();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="relative rounded-xl overflow-hidden"
      style={{
        background: isDarkMode ? 'rgba(15, 15, 25, 0.8)' : 'rgba(255, 255, 255, 0.9)',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'
      }}
    >
      {/* Header cliquable */}
      <button
        onClick={handleToggle}
        className={`w-full p-3 flex items-center justify-between bg-gradient-to-r ${category.color} transition-all`}
        data-testid={`category-button-${category.id}`}
      >
        <h3 className="text-sm font-bold text-white drop-shadow-sm flex items-center gap-2">
          <span className="text-lg">{category.emoji}</span>
          {category.name}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/80 font-medium bg-white/20 px-2 py-0.5 rounded-full">
            {category.apps.length}
          </span>
          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-4 h-4 text-white" />
          </motion.div>
        </div>
      </button>

      {/* Grid d'apps */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`p-3 grid grid-cols-4 sm:grid-cols-6 gap-1 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50/80'}`}>
              {category.apps.map((app, index) => (
                <AppIcon key={app.name} app={app} index={index} isDarkMode={isDarkMode} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// 🚀 Composant principal - Coffre de Survie Spatial
export default function AppsHub() {
  const { isDarkMode } = useTheme();
  const [isLockerOpen, setIsLockerOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [showInstrumentSelector, setShowInstrumentSelector] = useState(false);
  const [currentInstrument, setCurrentInstrument] = useState(smartMelody.getCurrentInstrument());

  const toggleLocker = useCallback(() => {
    if (!isLockerOpen) {
      playDepressurizationSound();
    }
    setIsLockerOpen(!isLockerOpen);
  }, [isLockerOpen]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  const toggleAll = () => {
    if (expandedCategories.length === APP_CATEGORIES.length) {
      setExpandedCategories([]);
    } else {
      setExpandedCategories(APP_CATEGORIES.map(cat => cat.id));
    }
  };

  const handleCloseInstrumentSelector = () => {
    setShowInstrumentSelector(false);
    setCurrentInstrument(smartMelody.getCurrentInstrument());
  };

  const allExpanded = expandedCategories.length === APP_CATEGORIES.length;
  const instrumentInfo = INSTRUMENTS[currentInstrument];
  const totalApps = APP_CATEGORIES.reduce((acc, cat) => acc + cat.apps.length, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
        boxShadow: isLockerOpen 
          ? '0 0 60px rgba(6, 182, 212, 0.3), 0 20px 40px rgba(0,0,0,0.5)'
          : '0 0 30px rgba(6, 182, 212, 0.1), 0 10px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(148, 163, 184, 0.1)'
      }}
      data-testid="apps-hub"
    >
      {/* Sélecteur d'instrument */}
      <InstrumentSelector isOpen={showInstrumentSelector} onClose={handleCloseInstrumentSelector} />

      {/* 🌠 Background stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.3 + 0.1
            }}
            animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* HUD corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-cyan-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-cyan-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-purple-500/50" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-purple-500/50" />

      {/* 🔐 HEADER COFFRE - Cliquable */}
      <motion.button
        onClick={toggleLocker}
        className="relative w-full p-4 flex items-center justify-between transition-all group"
        whileHover={{ backgroundColor: 'rgba(6, 182, 212, 0.05)' }}
        whileTap={{ scale: 0.995 }}
      >
        {/* Lumière de scan à l'ouverture */}
        <AnimatePresence>
          {isLockerOpen && (
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ scaleX: 0, opacity: 0 }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/10 to-transparent"
            />
          )}
        </AnimatePresence>

        <div className="relative z-10 flex items-center gap-4">
          {/* Icône coffre animée */}
          <motion.div
            className="w-14 h-14 rounded-xl flex items-center justify-center relative overflow-hidden"
            style={{
              background: isLockerOpen 
                ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)'
                : 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
              border: `2px solid ${isLockerOpen ? 'rgba(6, 182, 212, 0.5)' : 'rgba(6, 182, 212, 0.2)'}`
            }}
            animate={isLockerOpen ? { 
              boxShadow: ['0 0 20px rgba(6, 182, 212, 0.3)', '0 0 40px rgba(6, 182, 212, 0.5)', '0 0 20px rgba(6, 182, 212, 0.3)']
            } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div animate={{ rotate: isLockerOpen ? 0 : 0, scale: isLockerOpen ? 1.1 : 1 }}>
              {isLockerOpen ? (
                <Unlock className="w-7 h-7 text-cyan-400" />
              ) : (
                <Lock className="w-7 h-7 text-gray-500" />
              )}
            </motion.div>
            
            {/* LED status */}
            <motion.div
              className="absolute top-1 right-1 w-2 h-2 rounded-full"
              style={{ background: isLockerOpen ? '#10b981' : '#6b7280' }}
              animate={isLockerOpen ? { opacity: [1, 0.5, 1] } : {}}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </motion.div>

          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              🚀 Coffre de Survie
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                isLockerOpen ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500'
              }`}>
                {isLockerOpen ? 'OUVERT' : 'FERMÉ'}
              </span>
            </h3>
            <p className="text-[11px] text-gray-500 font-mono tracking-wider">
              {totalApps} ÉQUIPEMENTS • {APP_CATEGORIES.length} COMPARTIMENTS
            </p>
          </div>
        </div>

        {/* Controls à droite */}
        <div className="relative z-10 flex items-center gap-2">
          {/* Bouton Instrument */}
          <motion.button
            onClick={(e) => { e.stopPropagation(); setShowInstrumentSelector(true); }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-400"
          >
            <motion.span animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
              {instrumentInfo?.emoji || '🎵'}
            </motion.span>
          </motion.button>

          {/* Chevron */}
          <motion.div
            animate={{ rotate: isLockerOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30"
          >
            <ChevronDown className="w-5 h-5 text-cyan-400" />
          </motion.div>
        </div>
      </motion.button>

      {/* 📦 CONTENU DU COFFRE */}
      <AnimatePresence>
        {isLockerOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            {/* Barre de contrôle */}
            <div className="px-4 py-2 border-t border-b border-white/5 flex items-center justify-between bg-black/20">
              <span className="text-[10px] font-mono text-cyan-500/70 tracking-widest">EQUIPMENT BAY</span>
              <button
                onClick={toggleAll}
                className="text-[10px] px-3 py-1 rounded-full font-mono bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors border border-cyan-500/30"
              >
                {allExpanded ? '⬆ FERMER TOUT' : '⬇ OUVRIR TOUT'}
              </button>
            </div>

            {/* Catégories */}
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
              {APP_CATEGORIES.map((category, idx) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <AppCategory
                    category={category}
                    isExpanded={expandedCategories.includes(category.id)}
                    onToggle={() => toggleCategory(category.id)}
                    isDarkMode={isDarkMode}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between">
        <span className="text-[9px] font-mono text-gray-600 tracking-widest">SURVIVAL LOCKER v2.0</span>
        <div className="flex gap-1">
          <motion.div 
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: isLockerOpen ? '#06b6d4' : '#4b5563' }}
            animate={isLockerOpen ? { opacity: [1, 0.5, 1] } : {}}
            transition={{ duration: 1, repeat: Infinity }}
          />
          <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
          <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
        </div>
      </div>
    </motion.div>
  );
}
